import { useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Grid,
  GridItem,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Progress,
  Spinner,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import {
  TrendingUp,
  DollarSign,
  AlertTriangle,
  Activity,
  Download,
  Users,
  MessageCircle,
  Sparkles,
  Clock,
  Shield,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useAIUsage } from './useAIUsage';
import { Card } from '../../../components/Card';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';

/**
 * AI Usage Dashboard Page
 * Displays AI usage statistics, costs, and security events
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function AIUsagePage() {
  const history = useHistory();
  const { currentUser } = useAuth();
  const {
    loading,
    todayStats,
    monthStats,
    dailyStats,
    topUsers,
    securityIncidents,
    exportToCSV,
  } = useAIUsage();

  // Color mode values
  const headingColor = useColorModeValue('gray.900', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const labelColor = useColorModeValue('gray.500', 'gray.500');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.800');

  const checkAccess = useCallback(async () => {
    if (!currentUser) {
      history.push('/login');
      return;
    }

    try {
      if(!currentUser.SuperAdmin){
        history.push('/dashboard');
        return;
      }
    } catch (error) {
      console.error('Error checking access:', error);
      history.push('/dashboard');
    }
  }, [currentUser, history]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex align="center" justify="center" minH="60vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" />
            <Text color={textColor}>Loading AI usage statistics...</Text>
          </VStack>
        </Flex>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Flex align="center" justify="space-between" mb={8}>
        <Box>
          <Heading size="xl" color={headingColor} mb={2}>
            AI Usage Dashboard
          </Heading>
          <Text color={textColor}>
            Monitor AI costs, usage patterns, and security events
          </Text>
        </Box>
        <Button
          leftIcon={<Icon as={Download} />}
          colorScheme="blue"
          variant="outline"
          onClick={exportToCSV}
        >
          Export CSV
        </Button>
      </Flex>

      {/* Stats Cards */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6} mb={8}>
        {/* Total Interactions Card */}
        <GridItem>
          <Card>
            <Flex align="center" justify="space-between" mb={4}>
              <Box
                w={12}
                h={12}
                bg="blue.100"
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={Activity} w={6} h={6} color="blue.600" />
              </Box>
              <Text fontSize="sm" color={labelColor}>
                Today
              </Text>
            </Flex>
            <VStack align="start" spacing={1}>
              <Heading size="2xl" color={headingColor}>
                {todayStats?.totalInteractions || 0}
              </Heading>
              <Text fontSize="sm" color={textColor}>
                Total Interactions
              </Text>
              <HStack spacing={4} mt={2} fontSize="xs" color={labelColor}>
                <HStack spacing={1}>
                  <Icon as={Sparkles} w={3} h={3} />
                  <Text>{todayStats?.evaluationCount || 0}</Text>
                </HStack>
                <HStack spacing={1}>
                  <Icon as={MessageCircle} w={3} h={3} />
                  <Text>{todayStats?.chatCount || 0}</Text>
                </HStack>
              </HStack>
            </VStack>
          </Card>
        </GridItem>

        {/* Total Cost Card */}
        <GridItem>
          <Card>
            <Flex align="center" justify="space-between" mb={4}>
              <Box
                w={12}
                h={12}
                bg="green.100"
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={DollarSign} w={6} h={6} color="green.600" />
              </Box>
              <Text fontSize="sm" color={labelColor}>
                Today
              </Text>
            </Flex>
            <VStack align="start" spacing={1}>
              <Heading size="2xl" color={headingColor}>
                ${(todayStats?.totalCost || 0).toFixed(2)}
              </Heading>
              <Text fontSize="sm" color={textColor}>
                Total Cost
              </Text>
              <Text fontSize="xs" color={labelColor} mt={2}>
                ${(monthStats?.totalCost || 0).toFixed(2)} this month
              </Text>
            </VStack>
          </Card>
        </GridItem>

        {/* Avg Response Time Card */}
        <GridItem>
          <Card>
            <Flex align="center" justify="space-between" mb={4}>
              <Box
                w={12}
                h={12}
                bg="purple.100"
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={Clock} w={6} h={6} color="purple.600" />
              </Box>
              <Text fontSize="sm" color={labelColor}>
                Today
              </Text>
            </Flex>
            <VStack align="start" spacing={1}>
              <Heading size="2xl" color={headingColor}>
                {((todayStats?.avgResponseTime || 0) / 1000).toFixed(1)}s
              </Heading>
              <Text fontSize="sm" color={textColor}>
                Avg Response Time
              </Text>
              <Text fontSize="xs" color={labelColor} mt={2}>
                {((monthStats?.avgResponseTime || 0) / 1000).toFixed(1)}s this month
              </Text>
            </VStack>
          </Card>
        </GridItem>

        {/* Security Incidents Card */}
        <GridItem>
          <Card>
            <Flex align="center" justify="space-between" mb={4}>
              <Box
                w={12}
                h={12}
                bg="red.100"
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={AlertTriangle} w={6} h={6} color="red.600" />
              </Box>
              <Text fontSize="sm" color={labelColor}>
                Today
              </Text>
            </Flex>
            <VStack align="start" spacing={1}>
              <Heading size="2xl" color={headingColor}>
                {todayStats?.securityIncidents || 0}
              </Heading>
              <Text fontSize="sm" color={textColor}>
                Security Incidents
              </Text>
              <Text fontSize="xs" color={labelColor} mt={2}>
                {monthStats?.securityIncidents || 0} this month
              </Text>
            </VStack>
          </Card>
        </GridItem>
      </Grid>

      {/* Charts Section */}
      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6} mb={8}>
        {/* Usage Trend Card */}
        <GridItem>
          <Card>
            <HStack spacing={2} mb={6}>
              <Icon as={TrendingUp} w={5} h={5} color="blue.600" />
              <Heading size="md" color={headingColor}>
                Usage Trend (Last 30 Days)
              </Heading>
            </HStack>
            <VStack spacing={3} align="stretch">
              {dailyStats.slice(-7).map((day) => {
                const maxInteractions = Math.max(...dailyStats.map(d => d.interactions), 1);
                const percentage = Math.min(100, (day.interactions / maxInteractions) * 100);
                return (
                  <Box key={day.date}>
                    <Flex align="center" justify="space-between" mb={1}>
                      <Text fontSize="sm" color={textColor}>
                        {new Date(day.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" color={headingColor}>
                        {day.interactions} interactions
                      </Text>
                    </Flex>
                    <Progress
                      value={percentage}
                      colorScheme="blue"
                      size="sm"
                      borderRadius="full"
                      mb={1}
                    />
                    <Text fontSize="sm" color={textColor} textAlign="right">
                      ${day.cost.toFixed(2)}
                    </Text>
                  </Box>
                );
              })}
            </VStack>
          </Card>
        </GridItem>

        {/* Top Users Card */}
        <GridItem>
          <Card>
            <HStack spacing={2} mb={6}>
              <Icon as={Users} w={5} h={5} color="green.600" />
              <Heading size="md" color={headingColor}>
                Top Users (This Month)
              </Heading>
            </HStack>
            <VStack spacing={3} align="stretch">
              {topUsers.slice(0, 5).map((user, index) => (
                <Flex
                  key={user.user_id}
                  align="center"
                  justify="space-between"
                  py={2}
                  borderBottom="1px"
                  borderColor={borderColor}
                  _last={{ borderBottom: 'none' }}
                >
                  <HStack spacing={3}>
                    <Box
                      w={8}
                      h={8}
                      bgGradient="linear(to-br, blue.500, purple.600)"
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color="white"
                      fontWeight="semibold"
                      fontSize="sm"
                    >
                      {index + 1}
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium" color={headingColor}>
                        {user.user_name}
                      </Text>
                      <Text fontSize="xs" color={labelColor}>
                        {user.interactions} interactions
                      </Text>
                    </VStack>
                  </HStack>
                  <VStack align="end" spacing={0}>
                    <Text fontSize="sm" fontWeight="semibold" color={headingColor}>
                      ${user.total_cost.toFixed(2)}
                    </Text>
                    <Text fontSize="xs" color={labelColor}>
                      {(user.avg_response_time / 1000).toFixed(1)}s avg
                    </Text>
                  </VStack>
                </Flex>
              ))}
            </VStack>
          </Card>
        </GridItem>
      </Grid>

      {/* Security Incidents Table */}
      <Card>
        <HStack spacing={2} mb={6}>
          <Icon as={Shield} w={5} h={5} color="red.600" />
          <Heading size="md" color={headingColor}>
            Security Incidents
          </Heading>
        </HStack>
        {securityIncidents.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Icon as={Shield} w={12} h={12} color="green.500" mx="auto" mb={3} />
            <Text color={textColor}>No security incidents detected</Text>
          </Box>
        ) : (
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th color={textColor}>Date</Th>
                  <Th color={textColor}>User</Th>
                  <Th color={textColor}>Type</Th>
                  <Th color={textColor}>Issue</Th>
                  <Th color={textColor}>Details</Th>
                </Tr>
              </Thead>
              <Tbody>
                {securityIncidents.map((incident) => (
                  <Tr
                    key={incident.id}
                    _hover={{ bg: hoverBg }}
                    borderBottom="1px"
                    borderColor={borderColor}
                  >
                    <Td>
                      <Text fontSize="sm" color={textColor}>
                        {new Date(incident.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color={headingColor}>
                        {incident.user_name}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color={textColor}>
                        {incident.interaction_type === 'evaluation' ? 'Evaluation' : 'Chat'}
                      </Text>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        {incident.contained_pii && (
                          <Badge colorScheme="yellow">PII</Badge>
                        )}
                        {incident.injection_attempt && (
                          <Badge colorScheme="red">Injection</Badge>
                        )}
                      </HStack>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color={textColor}>
                        {incident.contained_pii && incident.pii_types.length > 0 && (
                          <Text>{incident.pii_types.join(', ')}</Text>
                        )}
                        {incident.injection_attempt && incident.injection_patterns.length > 0 && (
                          <Text>Pattern detected</Text>
                        )}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Container>
  );
}

